<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useInstagramAccounts } from '../../composables/useInstagramAccounts';
import FranchiseSelectMultiple from '../../inputs/FranchiseSelectMultiple.vue';

type Id = number | string;

interface InstagramPage {
  ig_user_id: string;
  ig_username: string;
  page_name?: string;
  access_token: string;
  profile_picture_url?: string;
}

interface PageState {
  franquia_ids: Id[];
  isConnecting: boolean;
}

interface ConnectedAccount {
  ig_username?: string;
}

interface ExchangeTokenResponse {
  accounts?: InstagramPage[];
}

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { exchangeToken, connectAccount, isLoading, error } = useInstagramAccounts() as unknown as {
  exchangeToken: (code: string, redirectUri: string) => Promise<unknown>;
  connectAccount: (data: Record<string, unknown>) => Promise<unknown>;
  isLoading: import('vue').Ref<boolean>;
  error: import('vue').Ref<string | null>;
};

const pages = ref<InstagramPage[]>([]);
const connectSuccess = ref<boolean>(false);
const connectedAccount = ref<ConnectedAccount | null>(null);

// Per-page state: selected franchise IDs and connecting flag
const pageState = ref<Record<string, PageState>>({});

onMounted(async () => {
  const code = route.query.code as string | undefined;
  if (!code) {
    error.value = t('Código de autorização não encontrado. Por favor, tente novamente.');
    return;
  }

  const redirectUri = 'https://localhost/instagram-oauth-callback';
  try {
    const data = await exchangeToken(code, redirectUri) as ExchangeTokenResponse | null;
    pages.value = data?.accounts || [];
    // Initialize per-page state
    pages.value.forEach(p => {
      pageState.value[p.ig_user_id] = { franquia_ids: [], isConnecting: false };
    });
  } catch (_) {}
});

const connect = async (page: InstagramPage): Promise<void> => {
  const state = pageState.value[page.ig_user_id];
  state.isConnecting = true;
  try {
    const result = await connectAccount({
      ig_user_id: page.ig_user_id,
      ig_username: page.ig_username,
      access_token: page.access_token,
      franquia_ids: state.franquia_ids,
    }) as ConnectedAccount;
    connectedAccount.value = result;
    connectSuccess.value = true;
  } catch (_) {
  } finally {
    state.isConnecting = false;
  }
};

const goToSettings = (): void => { router.push('/instagram-accounts'); };
</script>

<template>
  <div class="p-6 max-w-2xl mx-auto">
    <h1 class="text-2xl font-bold mb-4">{{ t('Conectar conta Instagram') }}</h1>

    <div v-if="isLoading" class="flex items-center gap-3 py-8 justify-center">
      <spinner style="width: 32px; height: 32px" />
      <span>{{ t('Carregando contas disponíveis...') }}</span>
    </div>

    <inline-message v-if="error" severity="error" class="mb-4">{{ error }}</inline-message>

    <!-- Success -->
    <div v-if="connectSuccess" class="text-center py-8">
      <i class="pi pi-check-circle text-5xl text-green-500 mb-4" style="display:block;" />
      <inline-message severity="success" class="mb-4 text-left">
        {{ t('Conta conectada com sucesso!') }} <strong>@{{ connectedAccount?.ig_username }}</strong>
      </inline-message>
      <p class="text-sm text-gray-500 mb-6">{{ t('Você já pode gerenciar mensagens Instagram pelo sistema.') }}</p>
      <Button
        :label="t('Ir para Contas Instagram')"
        icon="pi pi-arrow-right"
        data-testid="instagram-oauth-go-settings-btn"
        @click="goToSettings" />
    </div>

    <!-- Account + franchise selection -->
    <div v-else-if="pages.length > 0 && !isLoading">
      <p class="mb-4 text-gray-600">{{ t('Selecione a conta e as franquias que deseja associar:') }}</p>
      <div class="flex flex-col gap-4">
        <box v-for="page in pages" :key="page.ig_user_id" type="primary" :elem-id="`box_oauth_${page.ig_user_id}`">
          <!-- Account info -->
          <div class="flex items-center gap-3 mb-4">
            <img
              v-if="page.profile_picture_url"
              :src="page.profile_picture_url"
              style="width: 48px; height: 48px; border-radius: 50%; object-fit: cover; flex-shrink: 0;"
              :alt="page.ig_username"
            />
            <i v-else class="pi pi-instagram text-3xl text-purple-500" />
            <div>
              <p class="font-bold">@{{ page.ig_username }}</p>
              <p class="text-sm text-gray-500">{{ page.page_name }}</p>
            </div>
          </div>

          <!-- Franchise selector -->
          <div class="mb-4">
            <FranchiseSelectMultiple
              :label="t('Franquias')"
              :placeholder="t('Selecione as franquias')"
              :elem-id="`franquias_${page.ig_user_id}`"
              :data-testid="`instagram-oauth-franquias-select-${page.ig_user_id}`"
              :required="true"
              v-model="pageState[page.ig_user_id].franquia_ids">
            </FranchiseSelectMultiple>
          </div>

          <Button
            :label="t('Conectar')"
            icon="pi pi-link"
            :loading="pageState[page.ig_user_id]?.isConnecting"
            :disabled="!pageState[page.ig_user_id]?.franquia_ids?.length"
            :data-testid="`instagram-oauth-connect-btn-${page.ig_user_id}`"
            @click="connect(page)"
          />
        </box>
      </div>
    </div>

    <!-- No accounts -->
    <div v-else-if="!isLoading && !error && !connectSuccess">
      <inline-message severity="warn">
        {{ t('Nenhuma conta Instagram Business encontrada. Certifique-se de que sua página do Facebook está vinculada a uma conta Instagram Business.') }}
      </inline-message>
      <div class="mt-4">
        <Button
          :label="t('Tentar novamente')"
          icon="pi pi-refresh"
          outlined
          data-testid="instagram-oauth-retry-btn"
          @click="goToSettings" />
      </div>
    </div>
  </div>
</template>
